<script setup lang="ts">
    const props = defineProps<{
        modelValue: boolean;
        alreadyRegistered: boolean;
        submitted: boolean;
        submitting: boolean;
        comments: string;
        contactAboutBeta: boolean;
        canSubmitFeedback: boolean;
        error: string | null;
    }>();

    const emit = defineEmits<{
        (e: 'update:modelValue', value: boolean): void;
        (e: 'update:comments', value: string): void;
        (e: 'update:contactAboutBeta', value: boolean): void;
        (e: 'submit'): void;
    }>();

    const close = () => emit('update:modelValue', false);
</script>

<template>
    <div v-if="props.modelValue" class="early-access-dialog__overlay" role="presentation">
        <div
            class="early-access-dialog__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="landing-early-access-title"
            aria-describedby="landing-early-access-description"
            tabindex="-1"
            @keydown.esc="close">
            <button class="early-access-dialog__close" type="button" aria-label="Close" @click="close">×</button>

            <div class="early-access-dialog__top">
                <div class="early-access-dialog__badge" :class="{ 'is-new': !props.alreadyRegistered }">
                    <span class="early-access-dialog__emoji" aria-hidden="true">🎉</span>
                </div>

                <h3 id="landing-early-access-title" class="early-access-dialog__title">
                    <template v-if="props.alreadyRegistered">You’re already on the waitlist</template>
                    <template v-else>You’re on the list!</template>
                </h3>
                <p id="landing-early-access-description" class="early-access-dialog__subtitle">
                    <template v-if="props.alreadyRegistered">We’ll be in touch soon.</template>
                    <template v-else>Check your email for more details!</template>
                </p>
            </div>

            <div class="early-access-dialog__followup">
                <template v-if="props.submitted">
                    <p class="early-access-dialog__success" role="status">
                        <template v-if="props.alreadyRegistered">Thanks! Your message has been sent.</template>
                        <template v-else>Thanks! Your feedback has been sent.</template>
                    </p>
                </template>
                <template v-else>
                    <template v-if="props.alreadyRegistered">
                        <p class="early-access-dialog__followup-title">Have anything else to add?</p>
                        <p class="early-access-dialog__followup-note">
                            If you’d like, you can send us a quick note.
                            <span class="early-access-dialog__optional">(Optional, you can skip this.)</span>
                        </p>
                    </template>
                    <template v-else>
                        <p class="early-access-dialog__followup-title">Thanks for signing up!</p>
                        <p class="early-access-dialog__followup-note">
                            Want to tell us what you’re thinking?
                            <span class="early-access-dialog__optional">(Optional, you can skip this.)</span>
                        </p>
                    </template>

                    <textarea
                        id="landing-early-access-comments"
                        class="early-access-dialog__textarea"
                        rows="4"
                        :value="props.comments"
                        placeholder="Anything you want Mix n’ Batch to handle for you?"
                        :disabled="props.submitting"
                        @input="emit('update:comments', ($event.target as HTMLTextAreaElement).value)" />

                    <label v-if="!props.alreadyRegistered" class="early-access-dialog__checkbox">
                        <input
                            type="checkbox"
                            :checked="props.contactAboutBeta"
                            :disabled="props.submitting"
                            @change="emit('update:contactAboutBeta', ($event.target as HTMLInputElement).checked)" />
                        <span>Contact me about participating in the beta</span>
                    </label>

                    <p v-if="props.error" class="early-access-dialog__error" role="alert">{{ props.error }}</p>
                </template>
            </div>

            <div class="early-access-dialog__actions">
                <button class="early-access-dialog__action early-access-dialog__action--secondary" type="button" @click="close">
                    {{ props.submitted ? 'Close' : 'No thanks' }}
                </button>
                <button
                    v-if="!props.submitted"
                    class="early-access-dialog__action early-access-dialog__action--primary"
                    type="button"
                    :disabled="!props.canSubmitFeedback"
                    @click="emit('submit')">
                    <span v-if="props.submitting">Sending…</span>
                    <span v-else>{{ props.alreadyRegistered ? 'Send message' : 'Send feedback' }}</span>
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
    .early-access-dialog {
        &__overlay {
            position: fixed;
            inset: 0;
            background: rgba(15, 23, 42, 0.55);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 18px;
            z-index: 50;
        }

        &__dialog {
            position: relative;
            width: 100%;
            max-width: 560px;
            border-radius: 18px;
            background: var(--l2-dialog-bg, var(--landing-bg, #ffffff));
            color: var(--l2-text, var(--landing-text, #0f172a));
            box-shadow: 0 26px 70px rgba(15, 23, 42, 0.28);
            padding: 22px 22px 18px;
            text-align: left;
            box-sizing: border-box;
            border: 1px solid var(--l2-dialog-border, var(--landing-border, rgba(226, 232, 240, 0.9)));

            *,
            *::before,
            *::after {
                box-sizing: border-box;
            }
        }

        &__close {
            position: absolute;
            top: 10px;
            right: 10px;
            height: 36px;
            width: 36px;
            border: 0;
            border-radius: 10px;
            background: transparent;
            font-size: 26px;
            line-height: 1;
            cursor: pointer;
            color: var(--l2-text-muted, var(--landing-muted, rgba(71, 85, 105, 0.9)));

            &:hover {
                background: var(--l2-accent-soft, rgba(226, 232, 240, 0.7));
            }
        }

        &__top {
            display: grid;
            justify-items: center;
            text-align: center;
            gap: 8px;
            padding: 8px 8px 0;
        }

        &__badge {
            height: 64px;
            width: 64px;
            display: grid;
            place-items: center;
            border-radius: 999px;
            background: var(--landing-accent);
            color: #ffffff;

            &.is-new .early-access-dialog__emoji {
                animation: subtle-pop 2.6s ease-in-out infinite both;
            }
        }

        &__emoji {
            font-size: 30px;
            line-height: 1;
        }

        &__title {
            margin: 0;
            font-size: 20px;
            font-weight: 800;
            letter-spacing: -0.01em;
        }

        &__subtitle {
            margin: 0;
            color: var(--landing-muted);
            font-size: 14px;
        }

        &__followup {
            margin-top: 16px;
            border-top: 1px solid var(--landing-border, var(--l2-border-subtle, rgba(226, 232, 240, 0.9)));
            padding-top: 14px;
        }

        &__success {
            margin: 0;
            font-weight: 800;
            color: var(--landing-text);
        }

        &__followup-title {
            margin: 0 0 4px;
            font-weight: 800;
        }

        &__followup-note {
            margin: 0 0 12px;
            color: var(--landing-muted);
            font-size: 14px;
        }

        &__optional {
            display: inline-block;
            margin-left: 6px;
            font-weight: 600;
            color: var(--l2-text-subtle, var(--landing-muted, rgba(71, 85, 105, 0.9)));
        }

        &__field-label {
            display: block;
            margin: 10px 0 6px;
            font-size: 14px;
            font-weight: 700;
            color: var(--landing-text);
        }

        &__textarea {
            width: 100%;
            max-width: 100%;
            resize: vertical;
            min-height: 110px;
            padding: 12px 12px;
            background: var(--l2-surface, #ffffff);
            color: var(--l2-text, var(--landing-text, #0f172a));
            border: 1px solid var(--landing-border, var(--l2-border-subtle, rgba(226, 232, 240, 0.9)));
            border-radius: 14px;
            font-size: 15px;
            box-shadow: 0 10px 26px rgba(15, 23, 42, 0.05);
            outline: none;

            &:focus {
                border-color: rgba(114, 87, 209, 0.55);
                box-shadow:
                    0 10px 26px rgba(15, 23, 42, 0.05),
                    0 0 0 4px rgba(114, 87, 209, 0.12);
            }

            &:disabled {
                opacity: 0.7;
                cursor: not-allowed;
            }

            &::placeholder {
                color: var(--l2-text-subtle, rgba(100, 116, 139, 0.9));
            }
        }

        &__checkbox {
            display: flex;
            gap: 10px;
            align-items: center;
            margin-top: 12px;
            color: var(--landing-text);
            font-size: 14px;
            user-select: none;

            input {
                height: 18px;
                width: 18px;
                accent-color: var(--landing-accent);
            }
        }

        &__error {
            margin: 10px 0 0;
            font-size: 14px;
            color: #b91c1c;
        }

        &__actions {
            display: flex;
            justify-content: flex-end;
            gap: 12px;
            margin-top: 16px;
            padding-top: 12px;
            border-top: 1px solid var(--landing-border, var(--l2-border-subtle, rgba(226, 232, 240, 0.9)));
        }

        &__action {
            padding: 10px 14px;
            border-radius: 12px;
            font-weight: 800;
            font-size: 14px;
            cursor: pointer;
            border: 1px solid transparent;

            &--secondary {
                background: transparent;
                border-color: var(--landing-border, var(--l2-border-subtle, rgba(226, 232, 240, 0.9)));
                color: inherit;
            }

            &--primary {
                background: var(--landing-accent);
                color: #ffffff;
                border-color: var(--landing-accent);
            }

            &:disabled {
                opacity: 0.6;
                cursor: not-allowed;
            }
        }
    }

    @keyframes heartbeat {
        0% {
            transform: scale(1);
        }
        14% {
            transform: scale(1.3);
        }
        28% {
            transform: scale(1);
        }
        42% {
            transform: scale(1.3);
        }
        70% {
            transform: scale(1);
        }
    }

    @keyframes subtle-pop {
        0% {
            transform: scale(1);
        }
        16% {
            transform: scale(1.06);
        }
        32% {
            transform: scale(1);
        }
        100% {
            transform: scale(1);
        }
    }

    @media (max-width: 520px) {
        .early-access-dialog {
            &__actions {
                flex-direction: column-reverse;
                align-items: stretch;
            }
        }
    }
</style>
